'use client';

import { c as _c } from "react/compiler-runtime";
import { hashDiagnosticEntity, useActiveWallet, useClaimPrize, useTurnkeySigningDeps } from '@morfi/backend-sdk-v2/client';
import { D6_SCALE } from '@/config/solana';
import { logger } from '@/lib/telemetry/logger';
import { isDustD6 } from '@/lib/validation/dust';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface UsePrizePoolParams {
  marketPubkey: string;
  marketStatus: string;
  winnerOutcomeIndex: number | null;
  prizePoolRaw: string | null | undefined;
  claimTotalClaimableRaw: string | null | undefined;
  buyBurnPoolRaw: string | null | undefined;
  holdings: {
    outcomeIndex: number;
    balance: string;
    claimRightsBalanceD6: string;
    prizeClaimableD6: string;
    prizeClaimedD6: string;
    prizeState: 'none' | 'claimable' | 'claimed';
  }[];
}
interface UsePrizePoolResult {
  /** Lifetime prize pool in raw D6 — shared with every display surface. */
  prizePoolD6: string;
  /** Prize pool in display units (D6 decoded). */
  prizePoolAmount: number;
  /** Whether the prize pool has a non-zero displayable amount. */
  showPrizePool: boolean;
  /** Whether the market has settled into a terminal claim/result state. */
  locked: boolean;
  /** Whether the connected user owns winning claim rights. */
  isWinner: boolean;
  /** User's claimable amount in D6, or null if unavailable. */
  userShareD6: string | null;
  /** User's share of the lifetime prize pool, 0..1. */
  userSharePct: number;
  /** Whether the user has already claimed. */
  hasClaimed: boolean;
  /** Whether a claim transaction is in flight. */
  isClaimLoading: boolean;
  /** Submit the connected wallet's winning claim-rights balance. */
  claim: () => void;
  /** Show "distribution pending" text (resolved but not finalized). */
  showPendingText: boolean;
  /** Show the boost button (market is active, not resolved). */
  showBoostButton: boolean;
  /** True when market is resolved/finalized but the user only holds losing tokens. */
  isLoser: boolean;
}
type ClaimGate = {
  readonly blocked: string;
} | {
  readonly blocked: null;
  readonly wallet: NonNullable<ReturnType<typeof useActiveWallet>['activeWallet']>;
  readonly signingDeps: NonNullable<ReturnType<typeof useTurnkeySigningDeps>>;
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function d6ToDisplay(value: string | null | undefined): number {
  if (value === null || value === undefined) return 0;
  const n = Number(value);
  return Number.isNaN(n) ? 0 : n / D6_SCALE;
}
function hasPositiveClaimRights(value: string): boolean {
  return /^(?:[1-9]\d*)$/.test(value);
}
function positiveD6OrNull(value: string): string | null {
  return hasPositiveClaimRights(value) ? value : null;
}
function d6ShareFraction(partD6: string | null, totalD6: string): number {
  if (partD6 === null) return 0;
  try {
    const total = BigInt(totalD6);
    if (total <= BigInt(0)) return 0;
    const scaled = BigInt(partD6) * BigInt(D6_SCALE) / total;
    return Number(scaled) / D6_SCALE;
  } catch {
    // SILENT-CATCH: malformed indexed money fails closed to a 0% display;
    // claim eligibility still uses independently validated claim rights.
    return 0;
  }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Orchestrates all prize pool state for the market detail page.
 *
 * Combines the indexed position's claim state with market settlement state and
 * the SDK claim mutation into a single set of display-ready values.
 *
 * The buyback / drip-schedule split was removed when the prize-pool surface
 * was reshaped into a single hero card (no per-buyback breakdown UI).
 */
export function usePrizePool(t0) {
  const $ = _c(49);
  const {
    marketPubkey,
    marketStatus,
    winnerOutcomeIndex,
    prizePoolRaw,
    claimTotalClaimableRaw,
    buyBurnPoolRaw,
    holdings
  } = t0;
  const {
    activeWallet
  } = useActiveWallet();
  const signingDeps = useTurnkeySigningDeps();
  const prizeClaim = useClaimPrize();
  const isResolved = marketStatus === "resolved" || marketStatus === "graduated";
  let claimRightsD6;
  let hasClaimRights;
  let hasClaimed;
  let isWinner;
  let locked;
  let prizePoolAmount;
  let prizePoolD6;
  let t1;
  let userShareD6;
  let winningHolding;
  if ($[0] !== activeWallet?.address || $[1] !== buyBurnPoolRaw || $[2] !== claimTotalClaimableRaw || $[3] !== holdings || $[4] !== isResolved || $[5] !== marketPubkey || $[6] !== prizeClaim.data?.ok || $[7] !== prizeClaim.variables?.activeWallet || $[8] !== prizeClaim.variables?.marketAddress || $[9] !== prizePoolRaw || $[10] !== winnerOutcomeIndex) {
    prizePoolD6 = positiveD6OrNull(claimTotalClaimableRaw ?? "0") ?? positiveD6OrNull(buyBurnPoolRaw ?? "0") ?? positiveD6OrNull(prizePoolRaw ?? "0") ?? "0";
    prizePoolAmount = d6ToDisplay(prizePoolD6);
    locked = isResolved;
    winningHolding = winnerOutcomeIndex === null ? undefined : holdings.find(holding => Number(holding.outcomeIndex) === winnerOutcomeIndex);
    claimRightsD6 = winningHolding?.claimRightsBalanceD6 ?? "0";
    hasClaimRights = hasPositiveClaimRights(claimRightsD6);
    const claimConfirmedHere = prizeClaim.data?.ok === true && prizeClaim.variables?.marketAddress === marketPubkey && prizeClaim.variables?.activeWallet.address === activeWallet?.address;
    hasClaimed = winningHolding?.prizeState === "claimed" || claimConfirmedHere;
    isWinner = locked && (hasClaimRights || hasClaimed);
    userShareD6 = winningHolding === undefined ? null : hasClaimed ? positiveD6OrNull(winningHolding.prizeClaimedD6) ?? positiveD6OrNull(winningHolding.prizeClaimableD6) : positiveD6OrNull(winningHolding.prizeClaimableD6);
    t1 = d6ShareFraction(userShareD6, prizePoolD6);
    $[0] = activeWallet?.address;
    $[1] = buyBurnPoolRaw;
    $[2] = claimTotalClaimableRaw;
    $[3] = holdings;
    $[4] = isResolved;
    $[5] = marketPubkey;
    $[6] = prizeClaim.data?.ok;
    $[7] = prizeClaim.variables?.activeWallet;
    $[8] = prizeClaim.variables?.marketAddress;
    $[9] = prizePoolRaw;
    $[10] = winnerOutcomeIndex;
    $[11] = claimRightsD6;
    $[12] = hasClaimRights;
    $[13] = hasClaimed;
    $[14] = isWinner;
    $[15] = locked;
    $[16] = prizePoolAmount;
    $[17] = prizePoolD6;
    $[18] = t1;
    $[19] = userShareD6;
    $[20] = winningHolding;
  } else {
    claimRightsD6 = $[11];
    hasClaimRights = $[12];
    hasClaimed = $[13];
    isWinner = $[14];
    locked = $[15];
    prizePoolAmount = $[16];
    prizePoolD6 = $[17];
    t1 = $[18];
    userShareD6 = $[19];
    winningHolding = $[20];
  }
  const userSharePct = t1;
  let t2;
  if ($[21] !== activeWallet || $[22] !== hasClaimRights || $[23] !== hasClaimed || $[24] !== isWinner || $[25] !== prizeClaim.isPending || $[26] !== signingDeps || $[27] !== winningHolding) {
    t2 = function claimGate() {
      if (activeWallet === null || activeWallet === undefined) {
        return {
          blocked: "no_wallet"
        };
      }
      if (signingDeps === undefined) {
        return {
          blocked: "no_signing_deps"
        };
      }
      if (winningHolding === undefined) {
        return {
          blocked: "no_winning_holding"
        };
      }
      if (!isWinner) {
        return {
          blocked: "not_winner"
        };
      }
      if (!hasClaimRights) {
        return {
          blocked: "no_claim_rights"
        };
      }
      if (hasClaimed) {
        return {
          blocked: "already_claimed"
        };
      }
      if (prizeClaim.isPending) {
        return {
          blocked: "claim_in_flight"
        };
      }
      return {
        blocked: null,
        wallet: activeWallet,
        signingDeps
      };
    };
    $[21] = activeWallet;
    $[22] = hasClaimRights;
    $[23] = hasClaimed;
    $[24] = isWinner;
    $[25] = prizeClaim.isPending;
    $[26] = signingDeps;
    $[27] = winningHolding;
    $[28] = t2;
  } else {
    t2 = $[28];
  }
  const claimGate = t2;
  let t3;
  if ($[29] !== claimGate || $[30] !== claimRightsD6 || $[31] !== marketPubkey || $[32] !== prizeClaim) {
    t3 = function claim() {
      const gate = claimGate();
      if (gate.blocked !== null) {
        logger.warn("market.claim_prize_blocked", {
          marketHash: hashDiagnosticEntity(marketPubkey),
          reason: gate.blocked
        });
        return;
      }
      prizeClaim.mutate({
        marketAddress: marketPubkey,
        burnAmountD6: claimRightsD6,
        activeWallet: {
          id: gate.wallet.id,
          address: gate.wallet.address,
          type: gate.wallet.type
        },
        signingDeps: gate.signingDeps
      });
    };
    $[29] = claimGate;
    $[30] = claimRightsD6;
    $[31] = marketPubkey;
    $[32] = prizeClaim;
    $[33] = t3;
  } else {
    t3 = $[33];
  }
  const claim = t3;
  let t4;
  if ($[34] !== holdings) {
    t4 = holdings.some(_temp);
    $[34] = holdings;
    $[35] = t4;
  } else {
    t4 = $[35];
  }
  const hasAnyHoldings = t4;
  const isLoser = isResolved && locked && hasAnyHoldings && !isWinner;
  const showPendingText = !locked && isResolved;
  const showBoostButton = !locked && !isResolved;
  let t5;
  if ($[36] !== claim || $[37] !== hasClaimed || $[38] !== isLoser || $[39] !== isWinner || $[40] !== locked || $[41] !== prizeClaim.isPending || $[42] !== prizePoolAmount || $[43] !== prizePoolD6 || $[44] !== showBoostButton || $[45] !== showPendingText || $[46] !== userShareD6 || $[47] !== userSharePct) {
    t5 = {
      prizePoolD6,
      prizePoolAmount,
      showPrizePool: true,
      locked,
      isWinner,
      isLoser,
      userShareD6,
      userSharePct,
      hasClaimed,
      isClaimLoading: prizeClaim.isPending,
      claim,
      showPendingText,
      showBoostButton
    };
    $[36] = claim;
    $[37] = hasClaimed;
    $[38] = isLoser;
    $[39] = isWinner;
    $[40] = locked;
    $[41] = prizeClaim.isPending;
    $[42] = prizePoolAmount;
    $[43] = prizePoolD6;
    $[44] = showBoostButton;
    $[45] = showPendingText;
    $[46] = userShareD6;
    $[47] = userSharePct;
    $[48] = t5;
  } else {
    t5 = $[48];
  }
  return t5;
}
function _temp(h) {
  return !isDustD6(h.balance);
}